import { UseMutationResult, useMutation, useQuery } from '@tanstack/react-query';
import { APIError } from '@data';
import {
  LoginBody,
  LoginView,
  RefreshBody,
  PatientSignUpBody,
  VerifyBody,
  TrialManagerSignUpBody,
  AnonymousLoginView,
  AnonymousSignUpBody,
  AnonymousClaimBody,
  Language,
  ThemeType,
} from '@data/sdk/match';
import { useOptimisticSignUpStore } from '@data/storage';
import { MatchingApi } from '@data/utils/axios-instance';
import { pulse } from '@data/utils/pulse';

export const useLogin = (): UseMutationResult<LoginView, APIError<LoginBody>, LoginBody, unknown> =>
  useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.auth.login({ loginBody: values });
      return response.data;
    },
  });

export const useResetPassword = (): UseMutationResult<void, APIError, { email: string }, unknown> =>
  useMutation({
    mutationFn: async ({ email }) => {
      const response = await MatchingApi.auth.resetPassword({ resetPasswordBody: { email } });
      return response.data;
    },
  });

export const useSetPassword = (): UseMutationResult<
  LoginView,
  APIError,
  { code: string; password: string },
  unknown
> =>
  useMutation({
    mutationFn: async ({ code, password }) => {
      const response = await MatchingApi.auth.setPassword({ setPasswordBody: { code, password } });
      return response.data;
    },
  });

export const usePatientSignUp = (): UseMutationResult<
  LoginView,
  APIError<PatientSignUpBody>,
  PatientSignUpBody,
  unknown
> =>
  useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.auth.patientSignUp({ patientSignUpBody: values });
      return response.data;
    },
    // Definition-level so it survives the caller unmounting before the request resolves
    // (mutate-level callbacks don't).
    onSuccess: () => {
      pulse.signupCompleted({ method: 'email' });
    },
    onSettled: () => {
      useOptimisticSignUpStore.getState().setStatus(false);
    },
  });

export const useVerifyEmail = (): UseMutationResult<unknown, Error, VerifyBody, unknown> =>
  useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.auth.verify({ verifyBody: values });
      return response.data;
    },
  });

export const useRefreshSession = (): UseMutationResult<
  LoginView,
  APIError,
  RefreshBody,
  unknown
> => {
  return useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.auth.refresh({ refreshBody: values });
      return response.data;
    },
  });
};

export const useTrialManagerSignUp = (): UseMutationResult<
  LoginView,
  APIError<TrialManagerSignUpBody>,
  TrialManagerSignUpBody,
  unknown
> =>
  useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.auth.trialManagerSignUp({
        trialManagerSignUpBody: values,
      });
      return response.data;
    },
  });

// Login patient with no credentials
export const useAnonymousLogin = (values: AnonymousSignUpBody) =>
  useQuery<AnonymousLoginView, APIError>({
    queryKey: ['anonymous-login'],
    queryFn: async () => {
      const response = await MatchingApi.auth.anonymousSignUp({ anonymousSignUpBody: values });
      return response.data;
    },
  });

// Register user which was created anonymously
export const useCreateAnonymousUser = (): UseMutationResult<
  LoginView,
  APIError<AnonymousClaimBody>,
  { token: string; values: AnonymousClaimBody },
  unknown
> => {
  return useMutation({
    mutationFn: async ({ token, values }) => {
      const response = await MatchingApi.auth.anonymousClaim({ token, anonymousClaimBody: values });
      return response.data;
    },
  });
};

export const useAnonymousRequestClaim = (): UseMutationResult<
  unknown,
  APIError,
  { email: string },
  unknown
> => {
  return useMutation({
    mutationFn: async ({ email }) => {
      const response = await MatchingApi.auth.anonymousRequestClaim({ email });
      return response.data;
    },
  });
};

export const useGoogleSignUp = (): UseMutationResult<
  LoginView,
  APIError,
  { idToken: string },
  unknown
> =>
  useMutation({
    mutationFn: async ({ idToken }) => {
      const response = await MatchingApi.auth.googleSignUp({
        googleSignUpBody: {
          idToken,
          language: Language.En,
          theme: ThemeType.Curewiki,
        },
      });
      return response.data;
    },
  });

export const useGoogleLogin = (): UseMutationResult<
  LoginView,
  APIError,
  { idToken: string },
  unknown
> =>
  useMutation({
    mutationFn: async ({ idToken }) => {
      const response = await MatchingApi.auth.googleLogin({
        googleLoginBody: {
          idToken,
          language: Language.En,
        },
      });
      return response.data;
    },
  });
